import { useCallback, useMemo, useState } from "react";
import {
  DEFAULT_PAYOUT_PLANNING,
  RECOMMENDED_PAYOUT_OPTIONS,
  buildPayoutTableFromRecipe,
  isColumnPercentValid,
  isPayoutPlanningEmpty,
  isPayoutPlanningPartial,
  isRecommendedPayoutOptions,
  payoutTablesMatch,
} from "../../../utility/generateShapedPayoutTable";

const snapshotOf = (payoutData, cellsEdited) => ({
  payoutData: payoutData.map((row) => ({ ...row })),
  cellsEdited,
});

export const usePayoutWorkspace = ({ canEdit = true } = {}) => {
  const [showMore, setShowMore] = useState(false);
  const [advanced, setAdvanced] = useState({ ...RECOMMENDED_PAYOUT_OPTIONS });
  const [planning, setPlanning] = useState({ ...DEFAULT_PAYOUT_PLANNING });
  const [payoutData, setPayoutData] = useState(() =>
    buildPayoutTableFromRecipe(
      RECOMMENDED_PAYOUT_OPTIONS,
      DEFAULT_PAYOUT_PLANNING
    )
  );
  const [cellsEdited, setCellsEdited] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editSnapshot, setEditSnapshot] = useState(null);

  const isRecommended = useMemo(() => {
    if (cellsEdited) return false;
    if (!isPayoutPlanningEmpty(planning)) return false;
    if (!isRecommendedPayoutOptions(advanced)) return false;
    return payoutTablesMatch(
      payoutData,
      buildPayoutTableFromRecipe(
        RECOMMENDED_PAYOUT_OPTIONS,
        DEFAULT_PAYOUT_PLANNING
      )
    );
  }, [advanced, payoutData, planning, cellsEdited]);

  const optionsLocked = canEdit && cellsEdited;

  const calculateColumnTotal = (quantity) =>
    payoutData
      .filter(
        (row) =>
          row.payoutQuantity === parseInt(quantity, 10) &&
          row.payoutOrder <= parseInt(quantity, 10)
      )
      .reduce((sum, row) => sum + row.percentage, 0);

  const isColumnValid = (quantity) =>
    isColumnPercentValid(calculateColumnTotal(quantity));

  const allColumnsValid =
    payoutData.length === 0 ||
    [...new Set(payoutData.map((row) => row.payoutQuantity))].every(
      (quantity) => isColumnValid(quantity)
    );

  const hydrate = useCallback(
    ({
      options,
      planning: nextPlanning,
      payoutData: nextData,
      cellsEdited: nextCellsEdited = false,
    } = {}) => {
      const nextOptions = { ...(options || RECOMMENDED_PAYOUT_OPTIONS) };
      const nextPlan = { ...(nextPlanning || DEFAULT_PAYOUT_PLANNING) };
      setAdvanced(nextOptions);
      setPlanning(nextPlan);
      setCellsEdited(Boolean(nextCellsEdited));
      setPayoutData(
        Array.isArray(nextData) && nextData.length > 0
          ? nextData.map((row) => ({ ...row }))
          : buildPayoutTableFromRecipe(nextOptions, nextPlan)
      );
      setIsEditing(false);
      setEditSnapshot(null);
      setShowMore(false);
    },
    []
  );

  const handlePercentageChange = (quantity, order, value) => {
    if (!canEdit) return;
    if (value !== "" && !/^\d*\.?\d*$/.test(value)) return;
    if (
      value !== "" &&
      (Number.isNaN(parseFloat(value)) ||
        parseFloat(value) < 0 ||
        parseFloat(value) > 100)
    ) {
      return;
    }
    const nextPercentage =
      value === ""
        ? 0
        : Math.round((parseFloat(value) / 100) * 10000) / 10000;
    const existing = payoutData.find(
      (row) => row.payoutQuantity === quantity && row.payoutOrder === order
    );
    if (existing && existing.percentage === nextPercentage) return;
    setCellsEdited(true);
    setPayoutData((current) =>
      current.map((row) =>
        row.payoutQuantity === quantity && row.payoutOrder === order
          ? { ...row, percentage: nextPercentage }
          : row
      )
    );
  };

  const handleRoundWholeDollars = (checked) => {
    if (!canEdit) return;
    setPlanning((current) => ({
      ...current,
      roundToWholeDollars: Boolean(checked),
    }));
  };

  const handlePlanningField = (field, value) => {
    if (!canEdit || cellsEdited) return;
    const next = { ...planning, [field]: value };
    setPlanning(next);
    if (isPayoutPlanningPartial(next)) return;
    setPayoutData(buildPayoutTableFromRecipe(advanced, next));
  };

  const handleAdvancedField = (field, value) => {
    if (!canEdit || cellsEdited) return;
    const next = { ...advanced, [field]: value };
    setAdvanced(next);
    setPayoutData(buildPayoutTableFromRecipe(next, planning));
  };

  const handleResetRecommended = () => {
    if (!canEdit) return;
    const next = { ...RECOMMENDED_PAYOUT_OPTIONS };
    const emptyPlanning = { ...DEFAULT_PAYOUT_PLANNING };
    setAdvanced(next);
    setPlanning(emptyPlanning);
    setPayoutData(buildPayoutTableFromRecipe(next, emptyPlanning));
    setIsEditing(false);
    setCellsEdited(false);
    setEditSnapshot(null);
  };

  const handleAddColumn = () => {
    if (!canEdit) return;
    const quantities = payoutData.map((row) => row.payoutQuantity);
    const newQuantity = quantities.length ? Math.max(...quantities) + 1 : 1;
    const maxRows = Math.max(
      newQuantity,
      ...payoutData.map((row) => row.payoutOrder),
      1
    );
    const extra = [];
    for (let order = 1; order <= maxRows; order += 1) {
      extra.push({
        payoutQuantity: newQuantity,
        percentage: 0,
        payoutOrder: order,
      });
    }
    setPayoutData([...payoutData, ...extra]);
    setCellsEdited(true);
  };

  const handleDeleteColumn = (quantity) => {
    if (!canEdit) return;
    setPayoutData(payoutData.filter((row) => row.payoutQuantity !== quantity));
    setCellsEdited(true);
  };

  const handleStartEditCells = () => {
    setEditSnapshot(snapshotOf(payoutData, cellsEdited));
    setShowMore(true);
    setIsEditing(true);
  };

  const handleFinishEditing = () => {
    setIsEditing(false);
    setEditSnapshot(null);
  };

  const handleCancelEditing = () => {
    if (editSnapshot) {
      setPayoutData(editSnapshot.payoutData);
      setCellsEdited(editSnapshot.cellsEdited);
    }
    setIsEditing(false);
    setEditSnapshot(null);
  };

  return {
    showMore,
    setShowMore,
    advanced,
    planning,
    payoutData,
    cellsEdited,
    isEditing,
    isRecommended,
    optionsLocked,
    allColumnsValid,
    calculateColumnTotal,
    isColumnValid,
    hydrate,
    handlePercentageChange,
    handlePlanningField,
    handleRoundWholeDollars,
    handleAdvancedField,
    handleResetRecommended,
    handleAddColumn,
    handleDeleteColumn,
    handleStartEditCells,
    handleFinishEditing,
    handleCancelEditing,
  };
};
